// Admin status primitives. Keep this file independent of the lazy
// /admin/status route so shell component registration does not load the
// route body.

// A section: a card on the branded `card` theme at the card radius.
export const AdminStatusSection = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'radiusCard',
  theme: 'card',
  maxWidth: '100%',
  boxSizing: 'border-box',

  Title: {
    tag: 'h2',
    fontSize: 'A1',
    fontWeight: '700',
    color: 'title',
    margin: '0',
    // The heading is OVERRIDDEN on this child by every consumer
    // (`Section_X: { extends: 'AdminStatusSection', Title: { text: '{{ … }}' } }`).
    // It used to be a `title:` prop on the wrapper read here as
    // `el.parent.title` — the one shape with NO working polyglot rewrite
    // (docs/I18N-CONVENTION.md §1.2): a template is resolved to '' by the
    // static attribute pass, and a function is never auto-invoked because
    // `title` is a valid attribute on every tag. Overriding the child needs
    // no custom prop at all, and drops the stray native tooltip the wrapper
    // used to grow.
    text: '{{ admin.section.untitled | polyglot }}'
  },

  // A real list: every row is a list item, so a screen reader hears "list,
  // N items" and each row's own Copy button.
  Rows: {
    tag: 'ul',
    flow: 'y',
    gap: 'X',
    margin: '0',
    padding: '0',
    listStyle: 'none'
  }
}

// One diagnostic row: a LIST ITEM that shows a label, its hint and a value.
// The row itself is no control. Its copy action is its own 36 px button,
// named for the row ("Copy symbols_user_id"). The whole row used to be the
// <button>: a two-line control at 58 px that the census counted off the
// control scale (dev 8a4c8af39), whose name was the full row text.
export const AdminStatusRow = {
  tag: 'li',
  flow: 'x',
  align: 'center space-between',
  gap: 'Z1',
  width: '100%',
  padding: 'Y 0 Y Z',
  boxSizing: 'border-box',
  '@mobileL': {
    flow: 'y',
    align: 'start',
    gap: 'V'
  },

  Left: {
    flow: 'y',
    gap: 'V',
    minWidth: '0',
    Label: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '500',
      color: 'title',
      overflowWrap: 'anywhere',
      // KEY-IN-STATE: a row's `label` / `hint` may be a bundle key or a
      // server-supplied identifier (`public.profiles`, `symbols_user_id`).
      // polyglot returns anything that is not a key verbatim, so both shapes
      // render correctly through one call site.
      text: (el, s) => el.call('polyglot', s.label || '')
    },
    Hint: {
      hide: (el, s) => !s.hint,
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => el.call('polyglot', s.hint || '')
    }
  },

  Right: {
    flow: 'x',
    align: 'center',
    gap: 'Y',
    minWidth: '0',
    '@mobileL': { width: '100%', align: 'center space-between' },

    Value: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '500',
      // §8.5 rule 2 — every row above passes a raw brand hue as `tone`, which
      // as small ink measured 2.15:1 (green) / 2.45:1 (red) on the
      // gray14-class wash. Map the status hues onto the AA-safe ink pairs at
      // the single consumer; neutral tones ('title', 'caption') pass through.
      color: (el, s) =>
        ({ green: 'successInk', red: 'dangerInk', orange: 'warningInk' })[s.tone] ||
        s.tone ||
        'title',
      wordBreak: 'break-word',
      textAlign: 'right',
      '@mobileL': { textAlign: 'left' },
      text: (el, s) => s.value
    },

    // The row's one action: copy "label: value (hint)" for a bug report. A
    // quiet compact control: no fill at rest, the brand washes on hover and
    // press, 36 px square, named for its row.
    CopyBtn: {
      extends: 'Button',
      type: 'button',
      flexShrink: '0',
      minHeight: 'controlHCompact',
      minWidth: 'controlHCompact',
      padding: '0',
      background: 'transparent',
      color: 'caption',
      ':hover': { background: 'hover', color: 'title' },
      ':active': { background: 'selected' },
      'aria-label': (el, s) =>
        el
          .call('polyglot', 'admin.status.copyRow')
          .replace('{label}', el.call('polyglot', s.label || '')),
      title: (el, s) =>
        el
          .call('polyglot', 'admin.status.copyRow')
          .replace('{label}', el.call('polyglot', s.label || '')),
      Icon: { name: 'copy', boxSize: 'Z1' },
      onClick: (e, el, s) => {
        const label = el.call('polyglot', s.label || '')
        const hint = s.hint ? el.call('polyglot', s.hint) : ''
        const line = `${label}: ${s.value}${hint ? ` (${hint})` : ''}`
        const clip = typeof navigator !== 'undefined' ? navigator.clipboard : null
        if (!clip || typeof clip.writeText !== 'function') return
        // The write is a promise: a refused clipboard must not throw unhandled,
        // and "Copied" is said only after it lands.
        clip.writeText(line).then(
          () =>
            el.call('openNotification', {
              title: el.call('polyglot', 'admin.status.copied'),
              message: line,
              type: 'success'
            }),
          () => {}
        )
      }
    }
  }
}
